import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/**
 * modKit.ts (M128) — the toolbox every mod part is built from, tuned for a
 * phone budget:
 *
 *  - PartBuilder merges every primitive of a rigid part into ONE mesh per
 *    material bucket, colour baked into vertex colours, so a glove or a
 *    floodlight pylon is 1-3 draw calls, not 40.
 *  - clothMesh is a plane that waves in the VERTEX SHADER (three uniforms per
 *    frame, zero CPU) — flags, the tifo banner, the goal net, scarves.
 *  - the canvas textures (kit numbers, the banner with the DR crest, the net,
 *    the ball roundel, hair) are drawn once and cached where they can be.
 *
 * Every helper takes CAR-LOCAL coordinates (forward +Z, up +Y, right +X).
 */

export type Bucket = 'matte' | 'paint' | 'metal' | 'glow';

export interface ModColors {
  primary: number;
  secondary: number;
}

/** The materials one rig shares across all its parts (+ per-part extras it
 *  registers so a rebuild disposes everything). */
export class ModMaterials {
  readonly matte = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.62, metalness: 0.06 });
  readonly paint = new THREE.MeshPhysicalMaterial({
    vertexColors: true,
    roughness: 0.3,
    metalness: 0.3,
    clearcoat: 0.8,
    clearcoatRoughness: 0.25,
  });
  readonly metal = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.22, metalness: 0.92 });
  readonly glow = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false });
  private readonly extra: THREE.Material[] = [];
  private readonly textures: THREE.Texture[] = [];

  track<T extends THREE.Material>(m: T): T {
    this.extra.push(m);
    return m;
  }

  trackTex<T extends THREE.Texture>(t: T): T {
    this.textures.push(t);
    return t;
  }

  byBucket(b: Bucket): THREE.Material {
    return b === 'paint' ? this.paint : b === 'metal' ? this.metal : b === 'glow' ? this.glow : this.matte;
  }

  dispose(): void {
    for (const m of [this.matte, this.paint, this.metal, this.glow, ...this.extra]) m.dispose();
    for (const t of this.textures) t.dispose();
  }
}

export interface Place {
  rot?: [number, number, number] | THREE.Quaternion;
  scale?: [number, number, number];
  bucket?: Bucket;
  radius?: number;
}

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _v = new THREE.Vector3();
const _p = new THREE.Vector3();
const _c = new THREE.Color();
const UP = new THREE.Vector3(0, 1, 0);

/** Accumulates primitives (with a colour + placement) and merges them into
 *  one mesh per material bucket. */
export class PartBuilder {
  private readonly geos: Record<Bucket, THREE.BufferGeometry[]> = { matte: [], paint: [], metal: [], glow: [] };

  add(src: THREE.BufferGeometry, color: number, x: number, y: number, z: number, place: Place = {}): void {
    // mergeGeometries needs every piece indexed or none — RoundedBoxGeometry
    // is non-indexed while the primitives are indexed (measured: the merge
    // returned null and the whole bucket vanished), so everything goes flat.
    const geo = src.index ? src.toNonIndexed() : src;
    if (geo !== src) src.dispose();
    const q = place.rot instanceof THREE.Quaternion ? place.rot : _q.setFromEuler(_e.set(...(place.rot ?? [0, 0, 0])));
    const sc = place.scale ? _v.set(...place.scale) : _v.set(1, 1, 1);
    geo.applyMatrix4(_m.compose(_p.set(x, y, z), q, sc));
    for (const name of Object.keys(geo.attributes)) {
      if (name !== 'position' && name !== 'normal' && name !== 'uv') geo.deleteAttribute(name);
    }
    if (!geo.attributes.normal) geo.computeVertexNormals();
    if (!geo.attributes.uv) {
      geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(geo.attributes.position.count * 2), 2));
    }
    const n = geo.attributes.position.count;
    const arr = new Float32Array(n * 3);
    _c.set(color);
    for (let i = 0; i < n; i++) {
      arr[i * 3] = _c.r;
      arr[i * 3 + 1] = _c.g;
      arr[i * 3 + 2] = _c.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
    this.geos[place.bucket ?? 'matte'].push(geo);
  }

  box(w: number, h: number, d: number, x: number, y: number, z: number, color: number, place: Place = {}): void {
    const r = place.radius ?? 0;
    const geo = r > 0 ? new RoundedBoxGeometry(w, h, d, 2, Math.min(r, w / 2, h / 2, d / 2)) : new THREE.BoxGeometry(w, h, d);
    this.add(geo, color, x, y, z, place);
  }

  /** A cylinder along local Y (rTop at +Y). */
  cyl(rTop: number, rBot: number, h: number, x: number, y: number, z: number, color: number, place: Place = {}): void {
    this.add(new THREE.CylinderGeometry(rTop, rBot, h, 14, 1), color, x, y, z, place);
  }

  /** A cone along local Y, apex at +Y. */
  cone(r: number, h: number, x: number, y: number, z: number, color: number, place: Place = {}): void {
    this.add(new THREE.ConeGeometry(r, h, 12, 1), color, x, y, z, place);
  }

  sphere(r: number, x: number, y: number, z: number, color: number, place: Place = {}): void {
    this.add(new THREE.SphereGeometry(r, 14, 10), color, x, y, z, place);
  }

  /** A torus in the local XY plane (axis +Z). */
  torus(R: number, r: number, x: number, y: number, z: number, color: number, place: Place = {}): void {
    this.add(new THREE.TorusGeometry(R, r, 8, 20), color, x, y, z, place);
  }

  plane(w: number, h: number, x: number, y: number, z: number, color: number, place: Place = {}): void {
    this.add(new THREE.PlaneGeometry(w, h), color, x, y, z, place);
  }

  /** A strut from a to b. */
  rod(a: THREE.Vector3, b: THREE.Vector3, r: number, color: number, bucket: Bucket = 'matte'): void {
    const dir = _v.subVectors(b, a);
    const len = dir.length();
    if (len < 1e-5) return;
    const q = new THREE.Quaternion().setFromUnitVectors(UP, dir.normalize());
    this.add(new THREE.CylinderGeometry(r, r, len, 8, 1), color, (a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2, { rot: q, bucket });
  }

  /** A cone whose APEX sits at `apex` and whose base lies `h` along `dir`. */
  spike(apex: THREE.Vector3, dir: THREE.Vector3, rBase: number, h: number, color: number, bucket: Bucket = 'matte'): void {
    const d = _v.copy(dir).normalize();
    // ConeGeometry's apex is its +Y end: point +Y back AT the apex (-dir).
    const q = new THREE.Quaternion().setFromUnitVectors(UP, d.clone().negate());
    this.add(new THREE.ConeGeometry(rBase, h, 12, 1), color, apex.x + d.x * (h / 2), apex.y + d.y * (h / 2), apex.z + d.z * (h / 2), {
      rot: q,
      bucket,
    });
  }

  build(mats: ModMaterials): THREE.Group {
    const g = new THREE.Group();
    for (const b of ['matte', 'paint', 'metal', 'glow'] as const) {
      const list = this.geos[b];
      if (list.length === 0) continue;
      const merged = list.length === 1 ? list[0] : mergeGeometries(list, false);
      if (!merged) continue;
      const mesh = new THREE.Mesh(merged, mats.byBucket(b));
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      g.add(mesh);
    }
    return g;
  }
}

/** Quaternion helper: yaw about Y then tilt about X (the head-of-a-lamp pose). */
export function yawTilt(yaw: number, tilt: number): THREE.Quaternion {
  return new THREE.Quaternion().setFromEuler(new THREE.Euler(tilt, yaw, 0, 'YXZ'));
}

/** A rotation that turns +Y onto `dir`. */
export function alignY(dir: THREE.Vector3): THREE.Quaternion {
  return new THREE.Quaternion().setFromUnitVectors(UP, _v.copy(dir).normalize());
}

/** Dispose every geometry under a group (materials are the rig's job). */
export function disposeGeometries(root: THREE.Object3D): void {
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh || (o as THREE.SkinnedMesh).isSkinnedMesh) m.geometry?.dispose();
  });
}

// ---------------------------------------------------------------------------
// Cloth — a plane that waves in the vertex shader. Local x runs ALONG the
// cloth from its fixed edge (x = 0), local y runs DOWN from the top edge
// (0 → -h), z is the flutter. Three uniforms drive it per frame.
// ---------------------------------------------------------------------------

export interface ClothHandle {
  mesh: THREE.Mesh;
  time: { value: number };
  amp: { value: number };
  droop: { value: number };
  /** 0 = fully draped at rest, 1 = flying flat (cancels the baked drape). */
  lift: { value: number };
  /** The gravity axis in the cloth's LOCAL frame (default hangs along -y). */
  grav: THREE.Vector3;
}

const CLOTH_VERTEX = `
#include <begin_vertex>
{
  float cu = uv.x;
  float cv = uv.y;
  float wave = uAmp * pow(cu, 1.4) * sin(6.2832 * (1.4 * cu - uTime))
    + 0.35 * uAmp * cu * sin(6.2832 * (0.8 * cv + 0.6 * cu - 1.3 * uTime));
  transformed += mix(aDrape, aFly, uLift) + uGrav * (uDroop * pow(cu, 1.6)) + vec3(0.0, 0.0, wave);
}`;

/** Options for {@link clothMesh}: `drape(u, v)` bakes a per-vertex REST
 *  offset in the cloth's local frame (x along the cloth from its fixed edge,
 *  y across, z the flutter/gravity axis; u = 0..1 from the fixed edge, v =
 *  0..1 across) that `lift` (0..1) lets the wind cancel — how a cape lies on
 *  the bodywork and hangs off the tail at the grid, then flies flat. */
export interface ClothOpts {
  drape?: (u: number, v: number) => [number, number, number];
  /** M153: the offset `lift` = 1 blends TO (default: none — flat). The cape
   *  flies clear of a body taller than its rail this way. */
  fly?: (u: number, v: number) => [number, number, number];
}

export function clothMesh(w: number, h: number, nx: number, ny: number, mat: THREE.Material, opts: ClothOpts = {}): ClothHandle {
  const geo = new THREE.PlaneGeometry(w, h, nx, ny);
  geo.translate(w / 2, -h / 2, 0);
  const uvs = geo.attributes.uv as THREE.BufferAttribute;
  const bake = (fn: ClothOpts['drape']): Float32Array => {
    const out = new Float32Array(uvs.count * 3);
    if (!fn) return out;
    for (let i = 0; i < uvs.count; i++) {
      const [dx, dy, dz] = fn(uvs.getX(i), 1 - uvs.getY(i));
      out[i * 3] = dx;
      out[i * 3 + 1] = dy;
      out[i * 3 + 2] = dz;
    }
    return out;
  };
  geo.setAttribute('aDrape', new THREE.BufferAttribute(bake(opts.drape), 3));
  geo.setAttribute('aFly', new THREE.BufferAttribute(bake(opts.fly), 3));
  const u = {
    uTime: { value: 0 },
    uAmp: { value: 0 },
    uDroop: { value: 0 },
    uLift: { value: 0 },
    uGrav: { value: new THREE.Vector3(0, -1, 0) },
  };
  mat.onBeforeCompile = (shader): void => {
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        '#include <common>\nuniform float uTime;\nuniform float uAmp;\nuniform float uDroop;\nuniform float uLift;\nuniform vec3 uGrav;\nattribute vec3 aDrape;\nattribute vec3 aFly;',
      )
      .replace('#include <begin_vertex>', CLOTH_VERTEX);
  };
  mat.customProgramCacheKey = (): string => 'dr-mod-cloth3';
  const mesh = new THREE.Mesh(geo, mat);
  mesh.frustumCulled = false; // the wave moves it past its static bounds
  mesh.castShadow = false;
  return { mesh, time: u.uTime, amp: u.uAmp, droop: u.uDroop, lift: u.uLift, grav: u.uGrav.value };
}

// ---------------------------------------------------------------------------
// Canvas textures.
// ---------------------------------------------------------------------------

function hex(c: number): string {
  return `#${c.toString(16).padStart(6, '0')}`;
}

function makeCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('canvas 2d unavailable');
  return [c, ctx];
}

function tex(c: HTMLCanvasElement): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

const NUMBER_FONT = "900 300px 'Oswald', Impact, 'Arial Black', 'Helvetica Neue', Arial, sans-serif";

/** A jersey number: white with a dark keyline on a transparent 512×384. */
export function numberTexture(n: number): THREE.CanvasTexture {
  const [c, ctx] = makeCanvas(512, 384);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = NUMBER_FONT;
  ctx.lineJoin = 'round';
  ctx.lineWidth = 34;
  ctx.strokeStyle = '#14151a';
  ctx.strokeText(String(n), 256, 200);
  ctx.fillStyle = '#f6f6f2';
  ctx.fillText(String(n), 256, 200);
  return tex(c);
}

/** M154 polish r5: the box the number's INK fills in its texture (the
 *  digits and their dark outline; the rest is clear), as texture fractions
 *  (v up, as the decal's UVs run): a decal cropped to it carries the same
 *  digits on a smaller patch of paint. */
export interface InkBox {
  u0: number;
  u1: number;
  v0: number;
  v1: number;
}
const inkCache = new Map<number, InkBox>();
export function numberInk(n: number): InkBox {
  const hit = inkCache.get(n);
  if (hit) return hit;
  const t = numberTexture(n);
  const c = t.image as HTMLCanvasElement;
  const g = c.getContext('2d')!;
  const d = g.getImageData(0, 0, c.width, c.height).data;
  let x0 = c.width;
  let x1 = -1;
  let y0 = c.height;
  let y1 = -1;
  for (let y = 0; y < c.height; y++) {
    for (let x = 0; x < c.width; x++) {
      if (d[(y * c.width + x) * 4 + 3]! > 8) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  const box = x1 < 0 ? { u0: 0, u1: 1, v0: 0, v1: 1 } : { u0: x0 / c.width, u1: (x1 + 1) / c.width, v0: 1 - (y1 + 1) / c.height, v1: 1 - y0 / c.height };
  t.dispose();
  inkCache.set(n, box);
  return box;
}

let flagTex: THREE.CanvasTexture | null = null;
/** The quartered red/yellow corner flag (cached, shared by every flag). */
export function flagTexture(): THREE.CanvasTexture {
  if (flagTex) return flagTex;
  flagTex = cornerFlagTexture(0xd81e2b, 0xffd21f);
  return flagTex;
}

function luma(c: number): number {
  return (0.299 * ((c >> 16) & 255) + 0.587 * ((c >> 8) & 255) + 0.114 * (c & 255)) / 255;
}

/** A quartered corner flag in the ride's two colours (a near-identical pair
 *  falls back to the kit colour over dark so the quarters still read). */
export function cornerFlagTexture(primary: number, secondary: number): THREE.CanvasTexture {
  const b = Math.abs(luma(primary) - luma(secondary)) < 0.18 ? (luma(primary) > 0.5 ? 0x15161a : 0xf3f3ef) : secondary;
  const [c, ctx] = makeCanvas(128, 128);
  ctx.fillStyle = hex(primary);
  ctx.fillRect(0, 0, 128, 128);
  ctx.fillStyle = hex(b);
  ctx.fillRect(64, 0, 64, 64);
  ctx.fillRect(0, 64, 64, 64);
  return tex(c);
}

let netTex: THREE.CanvasTexture | null = null;
/**
 * The Goal Wing's net texture: the live build's, restored in M154 polish r3
 * (round 2's cord geometry read as white scaffolding and a picket rail from
 * the race camera; a coarser blended texture tried this round read as a
 * white comb from behind the pickup). Its "fringe of grey hairs" was the
 * NET's fault, not the texture's: on the low-decked cars it fell at the race
 * camera's own elevation and was seen edge on (partsSoft.goalNetSheet keeps
 * it steeper now).
 */
export function goalNetTexture(): THREE.CanvasTexture {
  if (netTex) return netTex;
  const [c, ctx] = makeCanvas(256, 256);
  ctx.strokeStyle = 'rgba(250,250,248,0.92)';
  ctx.lineWidth = 3;
  for (let i = 0; i <= 8; i++) {
    const p = (i / 8) * 256;
    ctx.beginPath();
    ctx.moveTo(p, 0);
    ctx.lineTo(p + 6 * Math.sin(i), 256);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, p);
    ctx.lineTo(256, p + 5 * Math.cos(i * 1.3));
    ctx.stroke();
  }
  netTex = tex(c);
  netTex.wrapS = netTex.wrapT = THREE.RepeatWrapping;
  return netTex;
}

let logoImg: HTMLImageElement | null = null;
let logoReady = false;
const logoWaiters: (() => void)[] = [];
/** The DR crest (the app icon), loaded once; callers redraw when it lands. */
function withLogo(cb: () => void): void {
  if (logoReady) {
    cb();
    return;
  }
  logoWaiters.push(cb);
  if (!logoImg) {
    logoImg = new Image();
    logoImg.onload = (): void => {
      logoReady = true;
      for (const w of logoWaiters.splice(0)) w();
    };
    logoImg.onerror = (): void => {
      logoWaiters.length = 0;
    };
    logoImg.src = `${import.meta.env.BASE_URL}icon-512.png`;
  }
}

/** The tifo banner: the kit colour, a white border, the DR crest centred
 *  (drawn as soon as the icon image is in — the texture updates itself). */
export function bannerTexture(primary: number): THREE.CanvasTexture {
  const W = 512;
  const H = 320;
  const [c, ctx] = makeCanvas(W, H);
  const draw = (): void => {
    ctx.fillStyle = hex(primary);
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = '#f3f3ef';
    ctx.lineWidth = 14;
    ctx.strokeRect(7, 7, W - 14, H - 14);
    const bh = H * 0.66;
    const bx = (W - bh) / 2;
    const by = (H - bh) / 2;
    if (logoReady && logoImg) {
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(bx, by, bh, bh, bh * 0.2);
      ctx.clip();
      ctx.drawImage(logoImg, bx, by, bh, bh);
      ctx.restore();
    } else {
      ctx.fillStyle = 'rgba(20,21,26,0.55)';
      ctx.beginPath();
      ctx.roundRect(bx, by, bh, bh, bh * 0.2);
      ctx.fill();
    }
  };
  draw();
  const t = tex(c);
  withLogo(() => {
    draw();
    t.needsUpdate = true;
  });
  return t;
}

/** The ink a design is drawn in over the cape's kit-colour ground: the kit's
 *  second colour, unless it would vanish against the first (a white kit's
 *  white, a red/red), then white or near-black by the ground's luma. */
function capeInk(primary: number, secondary: number): number {
  if (Math.abs(luma(primary) - luma(secondary)) >= 0.22) return secondary;
  return luma(primary) > 0.5 ? 0x15161a : 0xf3f3ef;
}

/** M154 polish r4: a design grows only where the band holds it below this
 *  share of the cloth's width (the coupe's 0.69, the muscle car's 0.76, the
 *  sports car's 0.70, the rebuilt pickup's 0.76: each came out under the live
 *  build's size). Today's pickup holds 0.84 (0.79 under its Goal Wing) and
 *  already reads at the live size: it keeps its layout. */
export const CAPE_GROW_BELOW = 0.78;

/** M154 polish r4: the most of the cloth's width a cape design may span
 *  (the hem's white border runs inside the outer 3.5%), and the most of its
 *  band along the cloth (5% clear at each end of the band). */
export const CAPE_FILL_ACROSS = 0.86;
export const CAPE_FILL_BAND = 0.9;
/** M154 polish r4: how far a design that grows into a short band may be
 *  flattened against its round shape (its height over its width, as a share
 *  of the round design's) before it stops growing instead. */
export const CAPE_SQUASH = 0.8;
/** The smiley's extent inside its square box (the face, its outline and the
 *  tongue): the share of the box it spans, across and along. */
export const SMILEY_EXTENT = 0.69;

/**
 * M154 polish r4 (pure; the texture and the tests share it): where the cape's
 * design box sits on the transposed canvas, in canvas fractions: `ky` its
 * size across the cloth (canvas y, a share of the cloth's width), `kx` along
 * it (canvas x, a share of the cloth's length), (`x0`, `y0`) its corner.
 *
 * M153: the box is square on the cloth (`stretch` times taller along it
 * where the cloth lies flat), sized to the smaller of the visible band
 * (`fit` of the cloth from `start`) and the width (`aspect` = width over
 * length). Polish r4: where the band is the smaller (the hem now stops above
 * the tail lamps: the coupe's design fell to 72% of the live build's width
 * from the race camera, the muscle car's alone to 80%), the design keeps the
 * size a full-length cloth gives it: it grows to the cloth's width (`extC` =
 * its extent across its box) and fills the band along the cloth up to
 * CAPE_FILL_BAND (`extA` = its extent along its box), flattened at most to
 * CAPE_SQUASH of its round shape. `wide` (≥ 1) = the width the design is
 * sized for over the cloth's (the bike's cape is narrowed to its tail; its
 * design keeps the live cape's size). A design the band and the width
 * already allow, or held at CAPE_GROW_BELOW of the width or more, is laid
 * out exactly as before.
 */
export function capeDesignBox(
  fit: number,
  aspect: number,
  stretch: number,
  start: number,
  extA = SMILEY_EXTENT,
  extC = SMILEY_EXTENT,
  wide = 1,
): { kx: number; ky: number; x0: number; y0: number } {
  const f = Math.max(0.2, Math.min(1, fit));
  const u0 = Math.max(0, Math.min(1 - f, start));
  const asp = Math.max(0.15, Math.min(4, aspect));
  const st = Math.max(1, Math.min(2.5, stretch));
  let ky = Math.min(1, f / (st * asp));
  let kx = st * ky * asp;
  const want = Math.min(Math.max(1, wide), CAPE_FILL_ACROSS / Math.max(0.05, extC));
  if (ky < want - 1e-9 && (ky < CAPE_GROW_BELOW || wide > 1)) {
    const round = kx / ky;
    const kx2 = Math.max(kx, Math.min(round * want, (CAPE_FILL_BAND * f) / Math.max(0.05, extA)));
    // (never flatter than CAPE_SQUASH of round: it grows less instead)
    const ky2 = Math.min(want, kx2 / (CAPE_SQUASH * round));
    if (ky2 > ky) {
      ky = ky2;
      kx = kx2;
    }
  }
  return { kx, ky, x0: u0 + f / 2 - kx / 2, y0: (1 - ky) / 2 };
}

/** The fan cape (M128c, owner): a kit-colour cloth with a white hem carrying
 *  one of three designs in the kit's second colour — a smiley with its
 *  tongue out, a bold SEE YA!, or a bold TOO SLOW! Drawn TRANSPOSED: the
 *  cloth's u runs from the roof toward the tail and its v across the car,
 *  and the chase camera sees it from above and behind, so a transpose is
 *  what reads upright from the driver's seat (measured on the M128b art). */
export function capeTexture(
  primary: number,
  secondary: number,
  style: number,
  fit = 1,
  aspect = 1,
  stretch = 1,
  start = 0,
  wide = 1,
): THREE.CanvasTexture {
  const S = 512;
  const [c, ctx] = makeCanvas(S, S);
  // `fit` = the fraction of the cloth's length the chase camera can SEE
  // (a monster truck's cape hangs from the tailgate top to the rails and
  // tucks the rest under — M128c centred "TOO SLOW!" on the whole cloth and
  // only TOO showed). The design is drawn into that band, aspect kept.
  // M154: `start` = where that band begins along the cloth (a cape over a
  // stock wing shows its design in the hang below the wing's crest).
  // M153: `aspect` = the cloth's width over its length. The canvas is square
  // but the cloth is not (the bike's cape is three times longer than wide,
  // the pickup's tailgate cape wider than long), so the design is scaled
  // separately along and across the cloth to stay ROUND on it.
  // M153: `stretch` = how much taller (along the cloth) than wide the design
  // is drawn ON the cloth — a cape lying nearly flat is foreshortened from
  // the race camera, so its design is drawn long to read round there.
  // (Polish r4: capeDesignBox lays it out; see there.)
  const text = style === 2 || style === 3;
  const lines = style === 2 ? ['SEE', 'YA!'] : ['TOO', 'SLOW!'];
  const font = (size: number): string => `italic 900 ${size}px "Arial Black", Impact, "Helvetica Neue", Helvetica, Arial, sans-serif`;
  let px = 190;
  let extA = SMILEY_EXTENT;
  let extC = SMILEY_EXTENT;
  if (text) {
    ctx.font = font(px);
    const widest0 = Math.max(...lines.map((l) => ctx.measureText(l).width));
    px = Math.floor(px * Math.min(1, 400 / Math.max(1, widest0)));
    ctx.font = font(px);
    const widest = Math.max(...lines.map((l) => ctx.measureText(l).width));
    // (two lines a gap of 1.02 px apart, each about 0.8 px tall, and the
    // outline round them)
    extA = Math.min(1, (px * 1.02 + px * 0.8 + px * 0.11) / S);
    extC = Math.min(1, (widest + px * 0.11) / S);
  }
  const { kx, ky, x0: bx0, y0: by0 } = capeDesignBox(fit, aspect, stretch, start, extA, extC, wide);
  const ink = capeInk(primary, secondary);
  const dark = luma(ink) > 0.5 ? 'rgba(10,10,14,0.55)' : 'rgba(255,255,255,0.45)';
  ctx.fillStyle = hex(primary);
  ctx.fillRect(0, 0, S, S);
  ctx.strokeStyle = '#f3f3ef';
  ctx.lineWidth = 12;
  ctx.strokeRect(6, 6, S - 12, S - 12);
  ctx.strokeStyle = 'rgba(0,0,0,0.28)';
  ctx.lineWidth = 3;
  ctx.strokeRect(16, 16, S - 32, S - 32);
  // Upright space: x right, y down, as the chase camera reads it — TRANSPOSED
  // onto the canvas (design y runs along the cloth = canvas x) and scaled
  // into the visible band, centred across the cloth.
  ctx.setTransform(0, ky, kx, 0, bx0 * S, by0 * S);
  if (text) {
    ctx.font = font(px);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    const gap = px * 1.02;
    const y0 = S / 2 - gap / 2;
    lines.forEach((l, i) => {
      const y = y0 + i * gap;
      ctx.lineWidth = px * 0.11;
      ctx.strokeStyle = dark;
      ctx.strokeText(l, S / 2, y);
      ctx.fillStyle = hex(ink);
      ctx.fillText(l, S / 2, y);
    });
  } else {
    // The smiley: a big round face in the ink, one eye winking, a wide grin
    // and a tongue poking out — the cheeky one (owner's first pick).
    const cx = S / 2;
    const cy = S / 2;
    const r = 172;
    ctx.fillStyle = hex(ink);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = dark;
    ctx.lineWidth = 8;
    ctx.stroke();
    ctx.fillStyle = hex(primary);
    ctx.strokeStyle = hex(primary);
    // eyes: left open, right winking
    ctx.beginPath();
    ctx.ellipse(cx - 62, cy - 48, 22, 30, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineCap = 'round';
    ctx.lineWidth = 16;
    ctx.beginPath();
    ctx.moveTo(cx + 36, cy - 48);
    ctx.lineTo(cx + 92, cy - 44);
    ctx.stroke();
    // grin
    ctx.lineWidth = 18;
    ctx.beginPath();
    ctx.arc(cx, cy + 6, 110, Math.PI * 0.15, Math.PI * 0.85);
    ctx.stroke();
    // mouth fill under the grin
    ctx.beginPath();
    ctx.arc(cx, cy + 6, 110, Math.PI * 0.15, Math.PI * 0.85);
    ctx.closePath();
    ctx.fill();
    // tongue: a rounded blob hanging from the right half of the mouth
    ctx.fillStyle = hex(primary);
    ctx.strokeStyle = hex(ink);
    ctx.lineWidth = 10;
    ctx.beginPath();
    ctx.moveTo(cx + 16, cy + 88);
    ctx.quadraticCurveTo(cx + 24, cy + 176, cx + 74, cy + 158);
    ctx.quadraticCurveTo(cx + 108, cy + 140, cx + 86, cy + 84);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.moveTo(cx + 52, cy + 100);
    ctx.lineTo(cx + 60, cy + 150);
    ctx.stroke();
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  return tex(c);
}

/** A supporter's scarf: kit colour / white stripes. */
export function scarfTexture(primary: number): THREE.CanvasTexture {
  const [c, ctx] = makeCanvas(256, 64);
  for (let i = 0; i < 8; i++) {
    ctx.fillStyle = i % 2 === 0 ? hex(primary) : '#f3f3ef';
    ctx.fillRect(i * 32, 0, 32, 64);
  }
  return tex(c);
}

let roundelTex: THREE.CanvasTexture | null = null;
/** A soccer-ball roundel on transparent (cached): the mud-flap badge. */
export function roundelTexture(): THREE.CanvasTexture {
  if (roundelTex) return roundelTex;
  const [c, ctx] = makeCanvas(128, 128);
  ctx.fillStyle = '#17181c';
  ctx.beginPath();
  ctx.arc(64, 64, 62, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#f4f4f0';
  ctx.beginPath();
  ctx.arc(64, 64, 56, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#17181c';
  const pent = (cx: number, cy: number, r: number, rot: number): void => {
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const a = rot + (i / 5) * Math.PI * 2;
      const x = cx + Math.cos(a) * r;
      const y = cy + Math.sin(a) * r;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
  };
  pent(64, 64, 15, -Math.PI / 2);
  for (let k = 0; k < 5; k++) {
    const a = -Math.PI / 2 + (k / 5) * Math.PI * 2 + Math.PI / 5;
    pent(64 + Math.cos(a) * 42, 64 + Math.sin(a) * 42, 11, a);
  }
  roundelTex = tex(c);
  return roundelTex;
}

let hairTex: THREE.CanvasTexture | null = null;
/** Hair strands: chestnut base, lighter highlight strands, and RAGGED TIPS
 *  cut into the alpha over the last third (v 0.68→1) so a bundle ends in
 *  wisps, not a cone (cached; drawn once per session). */
export function hairTexture(): THREE.CanvasTexture {
  if (hairTex) return hairTex;
  const W = 128;
  const Hh = 256;
  const [c, ctx] = makeCanvas(W, Hh);
  ctx.fillStyle = '#3a2414';
  ctx.fillRect(0, 0, W, Hh);
  const tones = ['#241408', '#4a2e18', '#6b4424', '#8a5a2e', '#a9743d', '#33200f', '#5c3a1f'];
  for (let i = 0; i < 110; i++) {
    const x = (i * 37.3 + (i % 3) * 2.1) % W;
    const w = 1 + ((i * 7) % 3);
    ctx.fillStyle = tones[i % tones.length] ?? '#4a2e18';
    ctx.fillRect(x, 0, w, Hh);
  }
  // Ragged tips: clear each 2px column from a hashed row in the last third.
  const tip0 = Math.round(Hh * 0.68);
  for (let x = 0; x < W; x += 2) {
    const h = ((x * 9301 + 49297) % 233280) / 233280;
    const cut = tip0 + Math.round(h * (Hh - tip0));
    ctx.clearRect(x, cut, 2, Hh - cut);
  }
  hairTex = tex(c);
  hairTex.wrapS = THREE.RepeatWrapping;
  hairTex.wrapT = THREE.ClampToEdgeWrapping;
  return hairTex;
}

let faceTex: THREE.CanvasTexture | null = null;
/** A cheering face on a white ground (the head's skin colour multiplies it):
 *  brows, two eyes, a wide open mouth. Painted at the texture centre, which
 *  a SphereGeometry maps to its +X side (cached). */
export function faceTexture(): THREE.CanvasTexture {
  if (faceTex) return faceTex;
  const [c, ctx] = makeCanvas(128, 128);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, 128, 128);
  const eye = (x: number): void => {
    ctx.fillStyle = '#f8f4ee';
    ctx.beginPath();
    ctx.ellipse(x, 56, 6.5, 5.2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#1a1410';
    ctx.beginPath();
    ctx.ellipse(x + 0.6, 56.6, 3.4, 3.8, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#3a2a1c';
    ctx.fillRect(x - 6, 45, 12, 2.2);
  };
  eye(54);
  eye(74);
  ctx.fillStyle = '#5a1b16';
  ctx.beginPath();
  ctx.ellipse(64, 80, 8.5, 7.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#f4efe6';
  ctx.fillRect(56.5, 73.5, 15, 3.2);
  ctx.fillStyle = '#c9605a';
  ctx.beginPath();
  ctx.ellipse(64, 85, 4.5, 2.2, 0, 0, Math.PI * 2);
  ctx.fill();
  faceTex = tex(c);
  return faceTex;
}

let sparkTex: THREE.CanvasTexture | null = null;
/** A soft hot dot for sparks and puffs (cached). */
export function sparkTexture(): THREE.CanvasTexture {
  if (sparkTex) return sparkTex;
  const [c, ctx] = makeCanvas(32, 32);
  const g = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.35, 'rgba(255,240,200,0.8)');
  g.addColorStop(1, 'rgba(255,200,120,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 32, 32);
  sparkTex = tex(c);
  return sparkTex;
}

/** A tiny pool of one-shot sprites (sparks, whistle puffs). */
export class SpritePool {
  readonly group = new THREE.Group();
  private readonly items: { s: THREE.Sprite; v: THREE.Vector3; life: number; max: number; grow: number }[] = [];

  constructor(n: number, color: number, size: number, mats: ModMaterials, additive = true) {
    const map = sparkTexture();
    for (let i = 0; i < n; i++) {
      const mat = mats.track(
        new THREE.SpriteMaterial({
          map,
          color,
          transparent: true,
          opacity: 0,
          depthWrite: false,
          blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
        }),
      );
      const s = new THREE.Sprite(mat);
      s.scale.setScalar(size);
      s.visible = false;
      this.group.add(s);
      this.items.push({ s, v: new THREE.Vector3(), life: 0, max: 1, grow: 0 });
    }
  }

  emit(at: THREE.Vector3, vel: THREE.Vector3, life: number, grow = 0): void {
    const it = this.items.find((i) => i.life <= 0) ?? this.items[0];
    if (!it) return;
    it.s.position.copy(at);
    it.v.copy(vel);
    it.life = it.max = life;
    it.grow = grow;
    it.s.visible = true;
    (it.s.material as THREE.SpriteMaterial).opacity = 1;
  }

  update(dt: number, gravity: number, drag: number, baseSize: number): void {
    for (const it of this.items) {
      if (it.life <= 0) continue;
      it.life -= dt;
      if (it.life <= 0) {
        it.s.visible = false;
        continue;
      }
      it.v.y -= gravity * dt;
      it.v.multiplyScalar(Math.max(0, 1 - drag * dt));
      it.s.position.addScaledVector(it.v, dt);
      const f = it.life / it.max;
      (it.s.material as THREE.SpriteMaterial).opacity = f;
      it.s.scale.setScalar(baseSize * (1 + it.grow * (1 - f)));
    }
  }
}
